import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Zap, Printer, CheckCircle2, Users, Eye } from "lucide-react";
import HistoryComparison from "@/components/HistoryComparison";
import OpticalCalculator from "@/components/OpticalCalculator";

/**
 * Shared examination form used by both Secretary and Doctor.
 * All state is managed by the parent via the `live` object from useLivePatient.
 */
const EYE_COLS = [
  { key: 'ucva', label: 'UCVA' },
  { key: 'sph',  label: 'SPH' },
  { key: 'cyl',  label: 'CYL' },
  { key: 'ax',   label: 'AX' },
  { key: 'bcva', label: 'BCVA' },
  { key: 'near', label: 'NEAR' },
  { key: 'iop',  label: 'IOP' },
];

const ExamForm = forwardRef(function ExamForm({
  patient, live, shortcuts = [],
  onSaveOnly, onSavePrint, onPreviewPrint, saving,
  otherEditorPresent = false,
  // Only ever passed by DoctorPage. When present, a private note section is
  // rendered — never rendered for the secretary, since the prop is simply
  // absent on her page.
  doctorPrivateNote, onDoctorPrivateNoteChange,
}, ref) {
  const { data, update, updateMany, setFocusedField } = live;
  // Same signal used everywhere else in this form to detect the doctor
  // context (see doctorPrivateNote/OpticalCalculator/HistoryComparison
  // gating below) — reused here so diagnosis/prescription follow the same
  // rule: read-only for the secretary, fully editable for the doctor.
  const isDoctor = !!onDoctorPrivateNoteChange;

  const applyNormal = () => {
    const normalEyeValues = {
      ucva: "6/6", sph: "0.00", cyl: "0.00", ax: "0",
      bcva: "6/6", near: "N6", iop: "14",
    };
    const eyeUpdates = ["right_eye", "left_eye"].flatMap((eye) =>
      Object.entries(normalEyeValues).map(([key, value]) => ({
        path: `${eye}.${key}`, value,
      }))
    );
    updateMany([
      ...eyeUpdates,
      { path: "right_eye.lid", value: "Normal" },
      { path: "right_eye.cornea", value: "Normal" },
      { path: "right_eye.lens", value: "Normal" },
      { path: "right_eye.retina", value: "Normal" },
      { path: "left_eye.lid", value: "Normal" },
      { path: "left_eye.cornea", value: "Normal" },
      { path: "left_eye.lens", value: "Normal" },
      { path: "left_eye.retina", value: "Normal" },
    ]);
  };

  const insertShortcut = (text) => {
    if (!isDoctor) return; // prescription is doctor-only — see gating above
    const next = data.prescription ? `${data.prescription}\n${text}` : text;
    update("prescription", next);
  };

  return (
    <div className="bg-white dark:bg-ink-900 rounded-2xl border border-slate-200 dark:border-ink-700 overflow-hidden">
      {/* Patient header */}
      <div className="p-6 border-b border-slate-100 dark:border-ink-700 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1F2937] dark:text-ink-50">{patient?.name}</h2>
          <p className="text-sm text-slate-500 dark:text-ink-300 mt-1">
            {patient?.age} سنة • {patient?.date}
            {otherEditorPresent && (
              <span className="ms-3 inline-flex items-center gap-1 text-emerald-600 text-xs">
                <Users className="w-3 h-3" /> يتم التعديل من الجهاز الآخر
              </span>
            )}
          </p>
        </div>
        <Button
          data-testid="quick-normal-button"
          onClick={applyNormal}
          variant="outline"
          className="border-[#0B6E4F] text-[#0B6E4F] dark:border-emerald-700 dark:text-emerald-400 hover:bg-[#F0FDF4] dark:hover:bg-emerald-950/40 gap-2"
        >
          <Zap className="w-4 h-4" />
          Normal (نقرة واحدة)
        </Button>
      </div>

      <div className="p-6">
        {/* Eye exam table */}
        <h3 className="text-sm font-bold text-slate-500 dark:text-ink-300 uppercase tracking-wider mb-3">قياسات النظر</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-[#5B3A7D] text-white text-xs font-semibold p-2 w-10 rounded-tr-lg"></th>
                {EYE_COLS.map((c, i) => (
                  <th key={c.key} className={`bg-[#5B3A7D] text-white text-xs font-semibold p-2 ${i === EYE_COLS.length - 1 ? 'rounded-tl-lg' : ''}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {['right_eye', 'left_eye'].map((eye, idx) => (
                <tr key={eye}>
                  <td className={`bg-[#5B3A7D] text-white text-center font-bold text-lg ${idx === 1 ? 'rounded-br-lg' : ''}`}>
                    {eye === 'right_eye' ? 'R' : 'L'}
                  </td>
                  {EYE_COLS.map((c) => {
                    const path = `${eye}.${c.key}`;
                    return (
                      <td key={c.key} className="border border-slate-100 dark:border-ink-700 p-1">
                        <input
                          data-testid={`${eye === 'right_eye' ? 'right' : 'left'}-eye-${c.key}-input`}
                          value={data[eye][c.key] || ""}
                          onChange={(e) => update(path, e.target.value)}
                          onFocus={() => setFocusedField(path)}
                          onBlur={() => setFocusedField(null)}
                          maxLength={50}
                          className="w-full text-center text-sm text-[#1F2937] dark:text-ink-50 bg-transparent border-0 focus:outline-none focus:bg-slate-50 dark:focus:bg-ink-800 p-2 rounded"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Lens-suggestion calculator — clinical decision aid for the doctor
            only. Only ever rendered on DoctorPage (mirrors the private-note
            gating below), so the secretary never sees it. */}
        {onDoctorPrivateNoteChange && (
          <OpticalCalculator rightEye={data.right_eye} leftEye={data.left_eye} age={patient?.age} />
        )}

        {/* Visit-history comparison is a clinical review tool (trend of
            SPH/CYL/diagnosis over time) — doctor-only, same gating as the
            optical calculator above. Previously this rendered for both
            roles, which is inconsistent with every other clinical tool in
            this form. */}
        {onDoctorPrivateNoteChange && (
          <HistoryComparison patientId={patient?.id} currentData={data} />
        )}

        {/* Lid/Cornea/Lens/Retina */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {['lid', 'cornea', 'lens', 'retina'].map(field => {
            const path = `right_eye.${field}`;
            return (
              <div key={field}>
                <label className="text-xs font-semibold text-slate-600 dark:text-ink-300 uppercase block mb-1.5">
                  {field}
                </label>
                <Input
                  data-testid={`exam-${field}-input`}
                  value={data.right_eye[field] || ""}
                  onChange={(e) => {
                    updateMany([
                      { path: `right_eye.${field}`, value: e.target.value },
                      { path: `left_eye.${field}`, value: e.target.value },
                    ]);
                  }}
                  onFocus={() => setFocusedField(path)}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Normal"
                  className="h-10 text-sm"
                />
              </div>
            );
          })}
        </div>

        {/* Shortcuts row — these insert text into the prescription, which
            is doctor-only, so the whole panel is pointless (and confusing)
            for the secretary. */}
        {isDoctor && shortcuts.length > 0 && (
          <div className="mt-6">
            <label className="text-xs font-bold text-slate-500 dark:text-ink-300 uppercase tracking-wider block mb-2">
              اختصارات سريعة
            </label>
            <div className="flex flex-wrap gap-2">
              {shortcuts.map(s => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`shortcut-inline-${s.id}`}
                  onClick={() => insertShortcut(s.text)}
                  style={{ background: s.color || '#5B3A7D' }}
                  className="text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                  title={s.text}
                >
                  {s.text.length > 30 ? s.text.substring(0, 30) + '…' : s.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Text sections */}
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 dark:text-ink-300 uppercase tracking-wider block mb-2">
              التشخيص {!isDoctor && <span className="normal-case text-slate-400 dark:text-ink-400">(تكتبه الطبيبة فقط)</span>}
            </label>
            <Textarea
              data-testid="diagnosis-textarea"
              value={data.diagnosis}
              onChange={(e) => isDoctor && update("diagnosis", e.target.value)}
              onFocus={() => setFocusedField("diagnosis")}
              onBlur={() => setFocusedField(null)}
              readOnly={!isDoctor}
              rows={2}
              maxLength={5000}
              className={`resize-none ${!isDoctor ? "bg-slate-50 dark:bg-ink-950 cursor-not-allowed" : ""}`}
              placeholder={isDoctor ? "التشخيص السريري..." : "لم تكتب الطبيبة تشخيصاً بعد"}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 dark:text-ink-300 uppercase tracking-wider block mb-2">
              الوصفة الطبية {!isDoctor && <span className="normal-case text-slate-400 dark:text-ink-400">(تكتبها الطبيبة فقط)</span>}
            </label>
            <Textarea
              data-testid="prescription-textarea"
              value={data.prescription}
              onChange={(e) => isDoctor && update("prescription", e.target.value)}
              onFocus={() => setFocusedField("prescription")}
              onBlur={() => setFocusedField(null)}
              readOnly={!isDoctor}
              rows={5}
              maxLength={10000}
              className={`resize-none font-mono text-sm ${!isDoctor ? "bg-slate-50 dark:bg-ink-950 cursor-not-allowed" : ""}`}
              placeholder={isDoctor ? "Rx..." : "لم تكتب الطبيبة وصفة بعد"}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 dark:text-ink-300 uppercase tracking-wider block mb-2">ملاحظات</label>
            <Textarea
              data-testid="notes-textarea"
              value={data.notes}
              onChange={(e) => update("notes", e.target.value)}
              onFocus={() => setFocusedField("notes")}
              onBlur={() => setFocusedField(null)}
              rows={2}
              maxLength={5000}
              className="resize-none"
              placeholder="ملاحظات إضافية..."
            />
          </div>

          {/* Secretary's internal note — visible to both roles, NEVER
              printed and NEVER shown to the patient. */}
          <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4">
            <label className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider block mb-2">
              ملاحظة السكرتيرة (داخلية، لا تُطبع ولا يراها المريض)
            </label>
            <Textarea
              data-testid="secretary-note-textarea"
              value={data.secretary_note}
              onChange={(e) => update("secretary_note", e.target.value)}
              onFocus={() => setFocusedField("secretary_note")}
              onBlur={() => setFocusedField(null)}
              rows={2}
              maxLength={2000}
              className="resize-none bg-white dark:bg-ink-900"
              placeholder="مثال: المريض متأخر عن موعده، أو له تحسس من دواء معيّن..."
            />
          </div>

          {/* Doctor's private note — only ever rendered on DoctorPage. */}
          {onDoctorPrivateNoteChange && (
            <div className="rounded-xl border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/30 p-4">
              <label className="text-xs font-bold text-[#5B3A7D] dark:text-violet-400 uppercase tracking-wider block mb-2">
                ملاحظتي الخاصة (تراها أنتِ فقط، لا تُطبع ولا تراها السكرتيرة)
              </label>
              <Textarea
                data-testid="doctor-private-note-textarea"
                value={doctorPrivateNote}
                onChange={(e) => onDoctorPrivateNoteChange(e.target.value)}
                rows={2}
                maxLength={2000}
                className="resize-none bg-white dark:bg-ink-900"
                placeholder="ملاحظة شخصية خاصة بك حول هذه الحالة..."
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-8 pt-6 border-t border-slate-100 dark:border-ink-700">
          {onSaveOnly && (
            <Button
              data-testid="save-patient-button"
              onClick={onSaveOnly}
              disabled={saving}
              variant="outline"
              className="flex-1 h-14 text-base font-semibold"
            >
              <CheckCircle2 className="w-5 h-5 ms-2" />
              حفظ فقط
            </Button>
          )}
          {onPreviewPrint && (
            <Button
              data-testid="preview-print-button"
              onClick={onPreviewPrint}
              disabled={saving}
              variant="outline"
              className="flex-1 h-14 text-base font-semibold border-[#5B3A7D] text-[#5B3A7D] dark:border-violet-500 dark:text-violet-400"
            >
              <Eye className="w-5 h-5 ms-2" />
              معاينة قبل الطباعة
            </Button>
          )}
          {onSavePrint && (
            <Button
              data-testid="save-print-button"
              onClick={onSavePrint}
              disabled={saving}
              className="flex-1 h-14 text-base font-semibold bg-[#5B3A7D] hover:bg-[#4A2E68]"
            >
              <Printer className="w-5 h-5 ms-2" />
              حفظ + طباعة الوصفة
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default ExamForm;
